/**
 * BoardV3 Draggable Task Box
 * Wrapper component that makes a task draggable with dnd-kit
 * Includes resize handles on left and right edges + bottom detail resize handle
 * Reads selection state and dispatches clicks directly to selectionStore.
 * Reads detailLevel and isDetailResizing from boardLayoutStore.
 */

import React, { memo, useRef } from 'react';
import { useDraggable } from '@dnd-kit/core';
import { useSelector } from '@xstate/store/react';
import type { Task, Employee } from '../../../types';
import type { ScheduledTask } from '../../scheduler';
import { BoardV3TaskBoxContent } from './BoardV3TaskBoxContent';
import { PADDINGS } from '../../hooks/useBoardTaskDetailLevel';
import { useBoardTaskResize } from '../../hooks/useBoardTaskResize';
import { selectionStore } from '../../stores/selectionStore';
import { popupStore } from '../../stores/popupStore';
import { useIsDetailResizing, useTaskDetailControls } from '../BoardLayoutContext';

interface BoardV3DraggableTaskBoxProps {
  task: Task;
  x: number;
  y: number;
  width: number;
  height: number;
  dayWidth: number;
  assignees: Employee[];
  scheduled?: ScheduledTask;
  detailLevel: import('../../hooks/useBoardTaskDetailLevel').TaskDetailLevel;
  onResize?: (taskId: string, newDurationDays: number, newStartDayDelta?: number) => void;
  onContextMenu?: (e: React.MouseEvent, dayOffset: number) => void;
  isDragging: boolean;
}

// ============================================
// Main Component
// ============================================

export const BoardV3DraggableTaskBox = memo(function BoardV3DraggableTaskBox({
  task,
  x,
  y,
  width,
  height,
  dayWidth,
  assignees,
  scheduled,
  detailLevel,
  onResize,
  onContextMenu: onContextMenuProp,
  isDragging,
}: BoardV3DraggableTaskBoxProps) {
  const isDetailResizing = useIsDetailResizing();
  const { handleResizeStart: onDetailResizeStart } = useTaskDetailControls();
  const { attributes, listeners, setNodeRef } = useDraggable({
    id: `task-${task.id}`,
    data: { type: 'task', task },
    disabled: false,
  });

  const isSelected = useSelector(selectionStore, s => s.context.selectedIds.includes(task.id));

  const wrapperRef = useRef<HTMLDivElement>(null);
  const { resizeSide, resizeOffset, isResizing, handleResizeStart } = useBoardTaskResize(width, dayWidth, task.id, onResize);

  const padding = PADDINGS[detailLevel];
  const innerHeight = height - padding * 2;

  const displayLeft = resizeOffset ? x + padding + resizeOffset.left : x + padding;
  const displayWidth = resizeOffset ? resizeOffset.width - padding * 2 : width - padding * 2;
  const innerWidth = Math.max(displayWidth, 20);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isResizing) return;
    if (selectionStore.getSnapshot().context.isBoxSelecting) return;
    if (e.shiftKey) {
      // Shift+click: only toggle selection, don't open modal
      e.preventDefault(); // prevent browser text selection
      selectionStore.send({ type: 'selection.toggle', taskId: task.id });
    } else {
      // Regular click: open edit modal
      popupStore.send({ type: 'popup.requestEditTask', taskId: task.id });
    }
  };

  const resizeHandleWidth = 6;

  return (
    <div
      ref={wrapperRef}
      data-task-id={task.id}
      style={{
        position: 'absolute',
        left: displayLeft,
        top: y + padding,
        width: innerWidth,
        height: innerHeight,
        pointerEvents: 'auto',
        userSelect: 'none',
        opacity: isDragging ? 0.3 : 1,
        transition: isDragging || isResizing ? 'none' : 'opacity 0.15s ease',
      }}
    >
      {/* Left resize handle */}
      {onResize && (
        <ResizeHandle
          side="left"
          isActive={resizeSide === 'left'}
          isResizing={isResizing}
          handleWidth={resizeHandleWidth}
          onMouseDown={handleResizeStart('left')}
        />
      )}

      {/* Main draggable content area */}
      <div
        ref={setNodeRef}
        {...listeners}
        {...attributes}
        onClick={handleClick}
        onContextMenu={(e) => {
          if (onContextMenuProp) {
            e.preventDefault();
            e.stopPropagation();
            const rect = wrapperRef.current?.getBoundingClientRect();
            const relativeX = rect ? e.clientX - rect.left : 0;
            const dayOff = Math.max(1, Math.round(relativeX / dayWidth));
            onContextMenuProp(e, dayOff);
          }
        }}
        style={{
          position: 'absolute',
          left: resizeHandleWidth,
          top: 0,
          right: resizeHandleWidth,
          bottom: 0,
          cursor: 'grab',
        }}
      />

      {/* Task content (visual only, not interactive) */}
      <div style={{ pointerEvents: 'none', width: '100%', height: '100%' }}>
        <BoardV3TaskBoxContent
          task={task}
          assignees={assignees}
          detailLevel={detailLevel}
          scheduled={scheduled}
          cardWidth={innerWidth}
          isSelected={isSelected}
          isResizing={isResizing}
        />
      </div>

      {/* Right resize handle */}
      {onResize && (
        <ResizeHandle
          side="right"
          isActive={resizeSide === 'right'}
          isResizing={isResizing}
          handleWidth={resizeHandleWidth}
          onMouseDown={handleResizeStart('right')}
        />
      )}

      {/* Bottom detail resize handle */}
      <BottomResizeHandle
        isActive={!!isDetailResizing}
        onMouseDown={onDetailResizeStart}
      />
    </div>
  );
});

// Reusable resize handle for left/right edges
function ResizeHandle({
  side,
  isActive,
  isResizing,
  handleWidth,
  onMouseDown,
}: {
  side: 'left' | 'right';
  isActive: boolean;
  isResizing: boolean;
  handleWidth: number;
  onMouseDown: (e: React.MouseEvent) => void;
}) {
  return (
    <div
      onMouseDown={onMouseDown}
      onMouseEnter={(e) => {
        if (!isResizing) e.currentTarget.style.backgroundColor = 'rgba(59, 130, 246, 0.3)';
      }}
      onMouseLeave={(e) => {
        if (!isResizing) e.currentTarget.style.backgroundColor = 'transparent';
      }}
      style={{
        position: 'absolute',
        [side]: 0,
        top: 0,
        width: handleWidth,
        height: '100%',
        cursor: 'col-resize',
        backgroundColor: isActive ? 'rgba(59, 130, 246, 0.5)' : 'transparent',
        borderRadius: side === 'left' ? '6px 0 0 6px' : '0 6px 6px 0',
        zIndex: 20,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div style={{
        width: 2,
        height: '40%',
        backgroundColor: isActive ? '#3b82f6' : 'rgba(255,255,255,0.5)',
        borderRadius: 1,
      }} />
    </div>
  );
}

// Bottom resize handle for detail level
function BottomResizeHandle({
  isActive,
  onMouseDown,
}: {
  isActive: boolean;
  onMouseDown: (e: React.MouseEvent) => void;
}) {
  return (
    <div
      onMouseDown={(e) => {
        e.stopPropagation();
        e.preventDefault();
        onMouseDown(e);
      }}
      onMouseEnter={(e) => {
        if (!isActive) e.currentTarget.style.backgroundColor = 'rgba(59, 130, 246, 0.3)';
      }}
      onMouseLeave={(e) => {
        if (!isActive) e.currentTarget.style.backgroundColor = 'transparent';
      }}
      style={{
        position: 'absolute',
        bottom: -2,
        left: 6,
        right: 6,
        height: 4,
        cursor: 'row-resize',
        zIndex: 21,
        backgroundColor: isActive ? 'rgba(59, 130, 246, 0.3)' : 'transparent',
      }}
    />
  );
}
